import React from 'react';
import DatesService from 'common/services/Dates';
import Utils from 'common/services/Utils';
import Carrousel from '../../../../widgets/Carrousel/Carrousel';

const Avatar = ({ url, alt, sender }) => {
  const letter =
    sender && typeof sender === 'string' && sender.length > 0
      ? sender[0].toUpperCase()
      : 'U';
  const bgColor = Utils.getRandomColor(sender || alt || 'U');

  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        overflow: 'hidden',
        background: url ? '#eee' : bgColor,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={alt || 'avatar'}
          width={32}
          height={32}
          loading="lazy"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 18 }}>
          {letter}
        </span>
      )}
    </div>
  );
};

const Stars = ({ rating = 0 }) => {
  const star =
    'https://app.goodays.co//media/widgets/images/commons/icn_star_on.svg';
  const size = 16;
  const round = Math.round(rating);
  return (
    <div
      className="sl-flex sl-ai-center sl-gap-s"
      style={{ marginBottom: 2 }}
      role="img"
      aria-label={`Note: ${rating} sur 5 étoiles`}
      itemProp="reviewRating"
      itemScope
      itemType="https://schema.org/Rating"
    >
      <meta itemProp="ratingValue" content={String(rating)} />
      <meta itemProp="bestRating" content="5" />
      {[...Array(round)].map((_, i) => (
        <img
          key={i}
          width={size}
          height={size}
          src={star}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
      ))}
    </div>
  );
};

const Review = ({ review }) => {
  const date = DatesService.toLocaleDateString(review.date, 'fr-FR');
  return (
    <article
      className="sl-flex sl-flex-col sl-gap-s"
      style={{
        height: '100%',
        background: 'var(--background-color)',
        borderRadius: '12px',
        padding: 'var(--gutter-m)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
      itemScope
      itemType="https://schema.org/Review"
    >
      <header
        className="sl-flex sl-ai-center sl-gap"
        style={{ marginBottom: 2 }}
      >
        <Avatar
          url={review.avatarUrl}
          alt={review.sender || 'Utilisateur'}
          sender={review.sender || 'Utilisateur'}
        />
        <div className="sl-flex sl-flex-col" style={{ flex: 1 }}>
          <strong
            className="sl-text-bold sl-text-l"
            style={{ fontSize: 15 }}
            itemProp="author"
          >
            {review.sender || 'Utilisateur'}
          </strong>
          <time
            className="sl-text-smaller sl-text-secondary"
            style={{ fontSize: 12 }}
            itemProp="datePublished"
            dateTime={review.date || ''}
            aria-label="Date de publication"
          >
            {date}
          </time>
        </div>
      </header>
      <Stars rating={review.rating || 0} />
      <p
        className="sl-text-m"
        style={{ color: '#222', fontSize: 14, marginBottom: 2 }}
        itemProp="reviewBody"
      >
        {review.content}
      </p>
      {review.visitDate && (
        <span
          className="sl-text-smaller sl-text-secondary"
          style={{ fontSize: 12 }}
        >
          {review.visitDate}
        </span>
      )}
    </article>
  );
};

const GooddaysReviews = ({ reviews = [] }) => {
  if (reviews.length === 0) {
    return null;
  }

  const maxNumberOfReviewsToShow = 3;
  const slicedReviews = reviews.slice(0, maxNumberOfReviewsToShow);

  return (
    <section
      style={{ width: '100%' }}
      aria-labelledby="reviews-section-title"
      itemProp="review"
      itemScope
      itemType="https://schema.org/Review"
    >
      <h2 id="reviews-section-title" className="sr-only">
        Avis clients
      </h2>
      <div className="desktop-only" style={{ width: '100%' }}>
        <div
          className="sl-flex sl-gap"
          role="list"
          aria-label="Liste des avis clients"
        >
          {slicedReviews.map((review, index) => (
            <div
              key={index}
              role="listitem"
              style={{ flex: '1 1 0', minWidth: 0 }}
            >
              <Review review={review} />
            </div>
          ))}
        </div>
      </div>
      <div className="mobile-only">
        <Carrousel
          items={slicedReviews}
          style={{ maxHeight: '600px', width: '100%' }}
          pagination={false}
          ariaLabel="Carousel des avis clients"
        >
          {(review, index) => {
            return (
              <div
                key={index}
                style={{
                  padding: '0 var(--gutter-xxl)',
                }}
                role="listitem"
              >
                <Review review={review} />
              </div>
            );
          }}
        </Carrousel>
      </div>
    </section>
  );
};

export default GooddaysReviews;
